import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCol,
  CRow,
  CForm,
  CFormLabel,
  CFormInput,
  CFormSelect,
  CButton,
  CSpinner,
} from "@coreui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  calculatePrice,
  clearAiState,
  clearProductDetail,
  createProduct,
  fetchCategories,
  fetchProductDetails,
  fetchSpecifications,
  fetchSubcategories,
  generateAiDescription,
  generateAiFaqs,
  generateDescription,
  updateProduct,
} from "../../features/seller/sellerSlice";
import TipTapEditor from "../../components/seller/TipTapEditor.jsx";
import { FaCut, FaMagic, FaTrash } from "react-icons/fa";
import AppToast from "../../components/seller/AppToast.jsx";
import LiveVideoCapture from "../../components/seller/LiveVideoCapture.jsx";
import CIcon from "@coreui/icons-react";
import { cilLightbulb } from "@coreui/icons";
import {
  clearBuyerMessages,
  editImage,
} from "../../features/user/userSlice.js";
import "./styles.css";
const AddNewProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { detail: productToEdit } = useSelector(
    (state) => state.seller.product
  );
  const categories = useSelector((state) => state.seller.categories.list);
  const subCategories = useSelector((state) => state.seller.subCategories.list);
  const [estimatedPrice, setEstimatedPrice] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [errorModal, setErrorModal] = useState({ visible: false, message: "" });
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState("info");
  const [toastMessage, setToastMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { kycList } = useSelector((state) => state.user);
  const kycType = kycList?.[0]?.kyc_type;
  const { generatingDescription, generatingFaqs, description, faqs } =
    useSelector((state) => state.seller.ai);

  const {
    editImageLoading,
    editImageError,
    editImageSuccess,
    editImageResult,
  } = useSelector((state) => state.user);
  const [aiPreview, setAiPreview] = useState(""); // holds AI preview URL
  const [useAiImage, setUseAiImage] = useState(false); // track which one is selected

  console.log("Product to edit:", productToEdit);
  const initialForm = {
    category_id: "",
    subcategory_id: "",
    name: "",
    description: "",
    currency: "",
    price: "",
    quantity: "",
    location: "",
    unit_of_measurement: "Kg",
    image: null,
    video: null,
    condition: "",
    incoterm: "",
    payment_term: "",
    lead_time: "",
    faqs: [],
    specifications: [],
    weight: "",
    latitude: "",
    longitude: "",
    captured_at: "",
  };

  // New: Track field-level errors
  const [formErrors, setFormErrors] = useState({
    category_id: null,
    subcategory_id: null,
    name: null,
    description: null,
    price: null,
    quantity: null,
    location: null,
    unit_of_measurement: null,
    image: null,
    condition: null,
    incoterm: null,
    payment_term: null,
    lead_time: null,
    weight: null,
    specifications: {},
  });

  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (!id) {
      dispatch(clearProductDetail());
      dispatch(clearAiState());
      dispatch(clearBuyerMessages());
      setForm(initialForm);
      setImagePreview("");
      // Reset AI description/faqs so they don't leak in
      setForm((prev) => ({ ...prev, description: "", faqs: [] }));
      dispatch(clearAiState());
    }
    if (id) {
      dispatch(fetchProductDetails(id))
        .unwrap()
        .catch((error) => {
          console.log("Failed to load product detail", error);
        });
    }
  }, [id, dispatch]);

  useEffect(() => {
    dispatch(fetchCategories())
      .unwrap()
      .catch((error) => {
        console.error("Failed to load categories", error);
      });
  }, [dispatch]);

  useEffect(() => {
    if (form.category_id) {
      dispatch(fetchSubcategories(form.category_id))
        .unwrap()
        .catch((error) => {
          console.log("Failed to load subcategories", error);
        });
    }
  }, [form.category_id, dispatch]);

  useEffect(() => {
    if (form.subcategory_id) {
      dispatch(fetchSpecifications(form.subcategory_id))
        .unwrap()
        .then((specs) => {
          setForm((prev) => {
            let specValues = {};
            if (productToEdit && Array.isArray(productToEdit.specifications)) {
              productToEdit.specifications.forEach((spec) => {
                specValues[spec.field?.id ?? spec.specification_field_id] =
                  spec.value;
              });
            }
            const formatted = specs.map((spec) => ({
              field_id: spec.id,
              label: spec.name,
              input_type: spec.input_type || "text",
              value: specValues[spec.id] ?? "",
            }));
            return { ...prev, specifications: formatted };
          });
        })
        .catch((error) => {
          console.log("Failed to load specifications", error);
        });
    }
  }, [form.subcategory_id, dispatch, productToEdit]);

  useEffect(() => {
    if (id && productToEdit) {
      setForm({
        category_id:
          productToEdit.category_id ?? productToEdit.category?.id ?? "",
        subcategory_id:
          productToEdit.subcategory_id ?? productToEdit.subcategory?.id ?? "",
        name: productToEdit.name ?? "",
        description: productToEdit.description ?? "",
        price: productToEdit.price ?? "",
        quantity: productToEdit.quantity ?? "",
        location: productToEdit.location ?? "",
        unit_of_measurement: productToEdit.unit_of_measurement ?? "",
        image: null,
        condition: productToEdit.condition?.toLowerCase() ?? "",
        incoterm: productToEdit.incoterms ?? "",
        payment_term: productToEdit.payment_terms ?? "",
        lead_time: productToEdit.lead_time ?? "",
        faqs: productToEdit.faqs ?? "",
        specifications: Array.isArray(productToEdit.specifications)
          ? productToEdit.specifications.map((spec) => ({
            field_id: spec.field?.id ?? spec.specification_field_id,
            input_type: spec.field?.input_type ?? "text",
            value: spec.value ?? "",
          }))
          : [],
        weight: "",
      });
      setImagePreview(productToEdit.image_url ?? "");
    }
  }, [productToEdit, id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));

    if (name === "image" && files && files[0]) {
      setForm({ ...form, [name]: files[0] });
      setImagePreview(URL.createObjectURL(files[0]));
    } else {
      setForm({ ...form, [name]: value }); // no parsing
    }
  };

  useEffect(() => {
    // Editing existing product
    if (id) {
      setForm((prev) => ({
        ...prev,
        description: description || prev.description,
        faqs: faqs?.length ? faqs : prev.faqs,
      }));
    } else {
      // Adding new product
      setForm((prev) => ({
        ...prev,
        description: description || "",
        faqs: faqs?.length
          ? faqs.filter((f) => f.question?.trim() || f.answer?.trim())
          : [],
      }));
    }
  }, [description, faqs, id]);

  const [typingIndex, setTypingIndex] = useState(0);

  useEffect(() => {
    if (generatingDescription && form.description) {
      setTypingIndex(0); // reset on new generation

      const interval = setInterval(() => {
        setTypingIndex((prev) => {
          if (prev >= form.description.length - 1) {
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 25); // typing speed in ms

      return () => clearInterval(interval);
    }
  }, [generatingDescription, form.description]);

  // console.log("Final form.quantity type:", typeof form.quantity, form.quantity);

  const validateForm = () => {
    const errors = {};
    console.log("validation errors:", errors);

    if (!form.category_id) errors.category_id = "Category is required";
    if (!form.subcategory_id) errors.subcategory_id = "Subcategory is required";
    if (!form.name.trim()) errors.name = "Product name is required";
    const plainText = form.description
      ?.replace(/<[^>]*>/g, "") // remove HTML tags
      .replace(/\s+/g, ""); // remove spaces

    if (!plainText) {
      errors.description = "Description is required";
    }
    if (!form.currency) errors.currency = "Currency is required";
    if (!form.location.trim()) errors.location = "Location is required";
    if (!form.price || Number(form.price) <= 0)
      errors.price = "Price must be greater than 0";
    if (!form.quantity || Number(form.quantity) <= 0)
      errors.quantity = "Quantity must be greater than 0";
    if (!form.unit_of_measurement)
      errors.unit_of_measurement = "Unit is required";
    if (!form.condition) errors.condition = "Condition is required";
    if (!form.incoterm) errors.incoterm = "Incoterm is required";
    if (!form.payment_term) errors.payment_term = "Payment term is required";
    if (!form.lead_time) errors.lead_time = "Lead time is required";
    if (kycType === "individual" && !form.video) {
      errors.video = "Video is required for individual KYC";
    }
    if (!id && !form.image) errors.image = "Product image is required"; // Only for create
    //  Validate Specifications
    const specErrors = {};
    if (form.specifications.length > 0) {
      form.specifications.forEach((spec, i) => {
        if (!spec.value?.trim()) {
          specErrors[i] = `Please enter ${spec.label}`;
        }
      });
      if (Object.keys(specErrors).length > 0) {
        errors.specifications = specErrors;
      }
    }
    setFormErrors(errors);
    setForm((prev) => ({ ...prev }));

    return Object.keys(errors).length === 0;
  };

  const handleEstimate = async () => {
    if (!form.subcategory_id || !form.weight) {
      setErrorModal({
        visible: true,
        message:
          "Please select a subcategory and enter the weight before calculating the price.",
      });
      return;
    }
    try {
      const payload = {
        subcategory_id: form.subcategory_id,
        weight: form.weight,
        currency: form.currency || "AUD", // Fallback to AUD for estimate if not selected, but shouldn't happen if validation is strict
      };
      const result = await dispatch(calculatePrice(payload)).unwrap();
      setEstimatedPrice(
        result?.estimated_price ? `$${result.estimated_price}` : ""
      );
    } catch (error) {
      setEstimatedPrice("");
      setErrorModal({
        visible: true,
        message: "Failed to calculate price",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // console.log("clicked");
    // Debug: Log form values before validation
    // console.log("Form values before validation:", form);

    if (!validateForm()) return;
    setSubmitting(true);
    let cleanedDescription = form.description;
    if (
      typeof cleanedDescription === "string" &&
      cleanedDescription.startsWith("<p>") &&
      cleanedDescription.endsWith("</p>")
    ) {
      cleanedDescription = cleanedDescription
        .replace(/^<p>|<\/p>$/g, "")
        .trim();
    }

    const payload = new FormData();
    for (let pair of payload.entries()) {
      console.log(pair[0], pair[1]);
    }

    Object.entries(form).forEach(([key, value]) => {
      if (key === "description") {
        payload.append(key, cleanedDescription);
      } else if (key === "condition" && typeof value === "string") {
        payload.append(key, value.toLowerCase());
      } else if (key === "specifications" && Array.isArray(value)) {
        value.forEach((spec, i) => {
          payload.append(`specifications[${i}][field_id]`, spec.field_id);
          payload.append(`specifications[${i}][value]`, spec.value);
        });
      } else if (key === "faqs" && Array.isArray(value)) {
        value.forEach((faq, i) => {
          payload.append(`faqs[${i}][question]`, faq.question);
          payload.append(`faqs[${i}][answer]`, faq.answer);
        });
      } else if (key === "image" || key === "aiFile") {
        // Skip, handle manually below
      } else if (value !== null && value !== undefined) {
        payload.append(key, value);
      }
    });

    // Add final image selection
    if (useAiImage && form.aiFile) {
      payload.append("image", form.aiFile);
    } else if (form.image) {
      payload.append("image", form.image);
    }

    const img = payload.get("image");
    if (img) {
      console.log("📸 Submitting image:", img.name || img);
    } else {
      console.log("⚠️ No image found in payload");
    }
    console.log(
      "Submitting image type:",
      useAiImage ? "AI Edited" : "Original"
    );

    // for (let pair of payload.entries()) {
    //   // console.log("Pair logs:", pair[0], pair[1]);
    // }

    try {
      let successMessage = "";
      let response;

      if (id && productToEdit) {
        response = await dispatch(
          updateProduct({ id: productToEdit.id, data: payload })
        ).unwrap();
        successMessage = response?.message || "Product updated successfully";
      } else {
        console.log("Product created:", payload);
        response = await dispatch(createProduct(payload)).unwrap();
        successMessage = response?.message || "Product created successfully";
        setForm(initialForm);
        setImagePreview("");
        setAiPreview(""); // clear AI preview
        setUseAiImage(false);
        setImagePreview("");
      }

      setToastType("success");
      setToastMessage(successMessage);
      setToastVisible(true);
      setTimeout(() => {
        navigate(-1);
      }, 2000); // wait 2 seconds to let alert show
    } catch (err) {
      console.error("🔥 FULL ERROR OBJECT:", err);
      let errorMsg = "Error submitting product: ";

      if (err?.response?.data) {
        const { message, errors } = err.response.data;

        // Log full backend error
        console.error("🔥 API Validation Errors:", errors);

        // Set individual field errors
        if (errors) {
          const fieldErrors = {};
          Object.entries(errors).forEach(([field, messages]) => {
            fieldErrors[field] = messages?.[0]; // take the first message
          });

          setFormErrors(fieldErrors);
          errorMsg += Object.values(errors)
            .map((arr) => arr.join(", "))
            .join(" | ");
        } else if (message) {
          errorMsg += message;
        } else {
          errorMsg += JSON.stringify(err.response.data);
        }
      } else {
        errorMsg += err?.message || "Unknown error occurred";
      }

      setToastType("error");
      setToastMessage(errorMsg);
      setToastVisible(true);
      setErrorModal({ visible: true, message: errorMsg });
    } finally {
      setSubmitting(false); // stop loader
    }
  };

  const [videoWasDeleted, setVideoWasDeleted] = useState(false);
  const handleVideoCaptured = (videoData) => {
    if (!videoData || videoData.deleted) {
      setForm((prev) => ({
        ...prev,
        video: null,
        latitude: "",
        longitude: "",
        captured_at: "",
      }));

      // Mark the video as deleted to prevent preview restoration
      setVideoWasDeleted(true);
    } else {
      const { blob, latitude, longitude, captured_at } = videoData;
      const file = new File([blob], "live-verification.webm", {
        type: "video/webm",
      });

      setForm((prev) => ({
        ...prev,
        video: file,
        latitude,
        longitude,
        captured_at,
      }));

      setVideoWasDeleted(false); // just in case user re-records
    }
  };

  const selectedCategory = categories.find(
    (cat) => String(cat.id) === String(form.category_id)
  );

  // Generate Description
  const handleGenerateDescription = () => {
    const payload = {
      name: form.name,
      category: selectedCategory?.name || "",
      condition: form.condition,
      quantity: form.quantity,
      unit: form.unit_of_measurement,
      location: form.location,
    };
    console.log("Payoad in Desc:", payload);
    dispatch(generateAiDescription(payload))
      .unwrap()
      .catch((err) => console.error("Failed:", err));
  };

  //  Generate FAQs
  const handleGenerateFaqs = () => {
    const payload = {
      name: form.name,
      category: selectedCategory?.name || "",
      condition: form.condition,
      quantity: form.quantity,
      unit: form.unit_of_measurement,
      location: form.location,
    };
    console.log("Payoad in Faqs:", payload);
    dispatch(generateAiFaqs(payload))
      .unwrap()
      .catch((err) => console.error("Failed to generate Faqs:", err));
  };

  // Image Edit
  const handleEdit = async (action) => {
    try {
      let fileToSend = null;

      if (form.image) {
        // User uploaded a new image
        fileToSend = form.image;
      } else if (imagePreview && !form.image) {
        // Editing existing product: convert preview URL to File
        const response = await fetch(imagePreview);
        const blob = await response.blob();
        fileToSend = new File([blob], "existing-image.png", {
          type: blob.type,
        });
      }

      if (!fileToSend) {
        console.warn("No image available for AI edit");
        return;
      }

      const result = await dispatch(
        editImage({ file: fileToSend, action })
      ).unwrap();

      if (result?.data?.edited_image) {
        const editedUrl = result.data.edited_image;

        // Show preview
        setAiPreview(editedUrl);

        // Convert back into File for form submission
        const resp = await fetch(editedUrl);
        const blob = await resp.blob();
        const aiFile = new File([blob], "ai-edited.png", { type: blob.type });

        setForm((prev) => ({ ...prev, aiFile }));
      }
    } catch (err) {
      console.error("AI edit failed:", err);
    }
  };

  return (
    <>
      <div className="container-fluid px-3">
        <CRow className="align-items-center mt-4 mb-3">
          <CCol>
            <h4 className="mb-0">
              {productToEdit ? "Edit Product" : "Add New Product"}
            </h4>
          </CCol>
          <CCol className="text-end">
            <CButton color="secondary" onClick={() => navigate(-1)}>
              Back
            </CButton>
          </CCol>
        </CRow>

        <CCard>
          <CCardBody>
            <CForm component="form" onSubmit={handleSubmit}>
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Category</CFormLabel>
                  <CFormSelect
                    name="category_id"
                    value={form.category_id}
                    onChange={handleChange}
                    options={[
                      { label: "Select", value: "" },
                      ...categories.map((cat) => ({
                        label: cat.name,
                        value: String(cat.id),
                      })),
                    ]}
                  />
                  {formErrors.category_id && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.category_id}
                    </div>
                  )}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Subcategory</CFormLabel>
                  <CFormSelect
                    name="subcategory_id"
                    value={form.subcategory_id}
                    onChange={handleChange}
                    options={[
                      { label: "Select", value: "" },
                      ...subCategories.map((sub) => ({
                        label: sub.name,
                        value: String(sub.id),
                      })),
                    ]}
                  />
                  {formErrors.subcategory_id && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.subcategory_id}
                    </div>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3 ">
                <CCol md={6}>
                  <CFormLabel>Product Name</CFormLabel>
                  <CFormInput
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                  />
                  {formErrors.name && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.name}
                    </div>
                  )}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Location</CFormLabel>
                  <CFormInput
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                  />
                  {formErrors.location && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.location}
                    </div>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3 ">
                <CCol md={6} className="d-flex">
                  <div className="w-100">
                    {/* Label + Button in the same row */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <CFormLabel className="mb-0">Description</CFormLabel>
                      <CButton
                        color="primary"
                        variant="outline"
                        size="sm"
                        className="d-flex align-items-center"
                        onClick={handleGenerateDescription}
                        disabled={generatingDescription}
                      >
                        {generatingDescription ? (
                          <>
                            <CSpinner size="sm" className="me-1" />{" "}
                            Generating...
                          </>
                        ) : (
                          <>
                            {/* Use CoreUI icon OR react-icons here */}
                            <CIcon
                              icon={cilLightbulb}
                              size="sm"
                              className="me-1"
                            />
                            Suggest Description
                          </>
                        )}
                      </CButton>
                    </div>

                    {/* Description Editor */}
                    <div className="editor-wrapper position-relative">
                      <TipTapEditor
                        content={form.description}
                        onChange={(value) =>
                          setForm({ ...form, description: value })
                        }
                      />
                      {generatingDescription && (
                        <div className="editor-loading-overlay d-flex justify-content-center align-items-center">
                          <div className="loading-box">
                            <div className="skeleton-line"></div>
                            <div className="skeleton-line"></div>
                            <div className="skeleton-line"></div>
                          </div>
                        </div>
                      )}
                    </div>

                    {formErrors.description && (
                      <div style={{ color: "red", fontSize: "0.9em" }}>
                        {formErrors.description}
                      </div>
                    )}
                  </div>
                </CCol>

                <CCol md={6}>
                  {/* Title stays same */}
                  <CFormLabel>Price Estimator</CFormLabel>
                  <div className="w-100 border rounded p-3">
                    {/* Weight Input */}
                    <div className="mb-3">
                      <CFormLabel>Weight (kg)</CFormLabel>
                      <CFormInput
                        type="number"
                        name="weight"
                        value={form.weight}
                        onChange={handleChange}
                        onBlur={handleChange}
                        min={0}
                      />
                      {formErrors.weight && (
                        <div style={{ color: "red", fontSize: "0.9em" }}>
                          {formErrors.weight}
                        </div>
                      )}
                    </div>

                    {/* Calculate Button */}
                    <div className="mb-3">
                      <CButton color="info" onClick={handleEstimate}>
                        Calculate
                      </CButton>
                    </div>

                    {/* Estimated Price */}
                    <div>
                      <CFormLabel>Estimated Price</CFormLabel>
                      <CFormInput
                        type="text"
                        value={estimatedPrice}
                        readOnly
                        placeholder="$0"
                        className="bg-white"
                      />
                    </div>
                  </div>
                </CCol>

                {/* <CCol md={6}>
                  <CCard className="bg-light mt-4">
                    <CCardBody className="h-100 d-flex flex-column justify-content-between">
                      <h5 className="mb-3">Price Estimator</h5>
                      <div className="d-flex gap-3 align-items-end">
                        <div style={{ flex: 1 }}>
                          <CFormLabel>Weight (kg)</CFormLabel>
                          <CFormInput
                            type="number"
                            name="weight"
                            value={form.weight}
                            onChange={handleChange}
                            onBlur={handleChange}
                            min={0}
                          />
                          {formErrors.weight && (
                            <div style={{ color: "red", fontSize: "0.9em" }}>
                              {formErrors.weight}
                            </div>
                          )}
                        </div>
                        <div>
                          <CButton
                            color="info"
                            className="mt-2"
                            onClick={handleEstimate}
                          >
                            Calculate
                          </CButton>
                        </div>
                        <div style={{ flex: 1 }}>
                          <CFormLabel>Estimated Price</CFormLabel>
                          <CFormInput
                            type="text"
                            value={estimatedPrice}
                            readOnly
                            placeholder="$0"
                            className="bg-white"
                          />
                        </div>
                      </div>
                    </CCardBody>
                  </CCard>
                </CCol> */}
              </CRow>

              <CRow className="mb-3">
                <CCol md={4}>
                  <CFormLabel>Currency</CFormLabel>
                  <CFormSelect
                    name="currency"
                    value={form.currency}
                    onChange={handleChange}
                    options={[
                      { label: "Select Currency", value: "" },
                      { label: "AUD", value: "AUD" },
                      { label: "USD", value: "USD" },
                    ]}
                  />
                  {formErrors.currency && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.currency}
                    </div>
                  )}
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Price</CFormLabel>
                  <CFormInput
                    type="number"
                    name="price"
                    value={form.price}
                    onChange={handleChange}
                    onBlur={handleChange}
                    min={0}
                  />
                  {formErrors.price && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.price}
                    </div>
                  )}
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Quantity</CFormLabel>
                  <CFormInput
                    type="number"
                    name="quantity"
                    value={form.quantity}
                    onChange={handleChange}
                    onBlur={handleChange}
                    min={0}
                  />
                  {formErrors.quantity && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.quantity}
                    </div>
                  )}
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Unit Type</CFormLabel>
                  <CFormSelect
                    name="unit_of_measurement"
                    value={form.unit_of_measurement}
                    onChange={handleChange}
                    options={[
                      { label: "Select", value: "" },
                      { label: "Kilogram (Kg)", value: "Kg" },
                      { label: "Gram (g)", value: "Gram" },
                      { label: "Ounce (OZ)", value: "OZ" },
                      { label: "Grams per Sq. Meter (GSM)", value: "GSM" },
                      { label: "Ton", value: "Ton" },
                      { label: "Metric Ton (MT)", value: "MT" },
                    ]}
                  />
                  {formErrors.unit_of_measurement && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.unit_of_measurement}
                    </div>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CCol md={4}>
                  <CFormLabel>Product Condition</CFormLabel>
                  <CFormSelect
                    name="condition"
                    value={form.condition}
                    onChange={handleChange}
                    options={[
                      { label: "Select Condition", value: "" },
                      { label: "New", value: "new" },
                      { label: "Used", value: "used" },
                      { label: "Refurbished", value: "refurbished" },
                    ]}
                  />
                  {formErrors.condition && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.condition}
                    </div>
                  )}
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Select Shipping (Incoterms)</CFormLabel>
                  <CFormSelect
                    name="incoterm"
                    value={form.incoterm}
                    onChange={handleChange}
                    options={[
                      { label: "Select Shipping (Incoterm)", value: "" },
                      {
                        label:
                          "FOB - Buyer handles shipping after goods leave port",
                        value: "FOB",
                      },
                      {
                        label:
                          "CIF - Seller pays shipping & insurance to buyer's port",
                        value: "CIF",
                      },
                      {
                        label:
                          "EXW - Buyer picks up goods from seller's location",
                        value: "EXW",
                      },
                      {
                        label:
                          "DDP - Seller pays all costs and delivers to buyer's door",
                        value: "DDP",
                      },
                      {
                        label:
                          "DAP - Seller delivers, buyer pays customs/duties",
                        value: "DAP",
                      },
                    ]}
                  />
                  {formErrors.incoterm && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.incoterm}
                    </div>
                  )}
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Payment Terms</CFormLabel>
                  <CFormSelect
                    name="payment_term"
                    value={form.payment_term}
                    onChange={handleChange}
                    options={[
                      { label: "Select Payment Term", value: "" },
                      {
                        label: "Advance Payment - Full payment before shipping",
                        value: "Advance",
                      },
                      {
                        label: "Net 30 - Pay within 30 days of delivery",
                        value: "Net 30",
                      },
                      {
                        label:
                          "Letter of Credit - Payment through a bank guarantee",
                        value: "LC",
                      },
                      {
                        label: "Cash on Delivery - Pay at the time of delivery",
                        value: "COD",
                      },
                    ]}
                  />
                  {formErrors.payment_term && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.payment_term}
                    </div>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol md={4}>
                  <CFormLabel>Lead Time (in days)</CFormLabel>
                  <CFormInput
                    name="lead_time"
                    type="number"
                    value={form.lead_time}
                    onChange={handleChange}
                    onBlur={handleChange}
                  />
                  {formErrors.lead_time && (
                    <div style={{ color: "red", fontSize: "0.9em" }}>
                      {formErrors.lead_time}
                    </div>
                  )}
                </CCol>
                {/* <CCol md={4}>
                  <CFormLabel>Product Video (Optional)</CFormLabel>
                  <CFormInput
                    type="file"
                    name="video"
                    accept="video/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setForm((prev) => ({ ...prev, video: file }));
                        setVideoPreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                  {videoPreview && (
                    <div className="mt-2">
                      <CFormLabel>Preview:</CFormLabel>
                      <video
                        width="100%"
                        height="240"
                        controls
                        style={{
                          borderRadius: "8px",
                          border: "2px solid #868e96",
                        }}
                      >
                        <source src={videoPreview} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  )}
                </CCol> */}
              </CRow>
              <CCard className="mb-4">
                <CCardBody>
                  <h5 className="mb-4">Product Media</h5>

                  {/* Image Upload */}
                  <CRow className="mb-4">
                    <CCol xs={12}>
                      <CFormLabel>Product Image</CFormLabel>
                      <CFormInput
                        type="file"
                        name="image"
                        accept=".jpg,.jpeg,.png"
                        onChange={handleChange}
                        style={{ width: "100%" }} // full width
                      />
                      {formErrors.image && (
                        <div
                          style={{
                            color: "red",
                            fontSize: "0.9em",
                            marginTop: "6px",
                          }}
                        >
                          {formErrors.image}
                        </div>
                      )}
                    </CCol>
                  </CRow>

                  {/* Image Preview */}
                  <CRow className="mb-3">
                    <CCol xs={12} md={6}>
                      <CFormLabel>Original Preview</CFormLabel>
                      {imagePreview ? (
                        <div className="position-relative">
                          <div
                            style={{
                              width: "100%",
                              height: "240px",
                              border: useAiImage
                                ? "2px solid #868e96"
                                : "2px solid #0d6efd",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#f8f9fa",
                            }}
                          >
                            <img
                              src={imagePreview}
                              alt="Original"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                              }}
                            />
                            {!useAiImage && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  right: "8px",
                                  background: "#0d6efd",
                                  color: "white",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontSize: "0.8rem",
                                }}
                              >
                                ✔ Selected
                              </span>
                            )}
                          </div>
                          <CButton
                            color={useAiImage ? "secondary" : "primary"}
                            variant={useAiImage ? "outline" : "solid"}
                            size="sm"
                            className="mt-2"
                            onClick={() => setUseAiImage(false)}
                          >
                            Use Original
                          </CButton>
                        </div>
                      ) : (
                        <div
                          style={{
                            width: "100%",

                            height: "240px",
                            border: "2px dashed #868e96",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",

                            justifyContent: "center",
                            color: "#868e96",
                          }}
                        >
                          No image selected
                        </div>
                      )}
                    </CCol>

                    <CCol xs={12} md={6}>
                      <CFormLabel>AI Edited Preview</CFormLabel>
                      {aiPreview ? (
                        <div className="position-relative">
                          <div
                            style={{
                              width: "100%",
                              height: "240px",
                              border: useAiImage
                                ? "2px solid #0d6efd"
                                : "2px solid #868e96",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#f0f8ff",
                            }}
                          >
                            <img
                              src={aiPreview}
                              alt="AI Edited"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                              }}
                            />
                            {useAiImage && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  right: "8px",
                                  background: "#0d6efd",
                                  color: "white",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontSize: "0.8rem",
                                }}
                              >
                                ✔ Selected
                              </span>
                            )}
                          </div>
                          <CButton
                            color={useAiImage ? "primary" : "secondary"}
                            variant={useAiImage ? "solid" : "outline"}
                            size="sm"
                            className="mt-2"
                            onClick={() => setUseAiImage(true)}
                          >
                            Use AI Image
                          </CButton>
                        </div>
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "240px",
                            border: "2px dashed #868e96",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#868e96",
                          }}
                        >
                          No AI edit yet
                        </div>
                      )}

                      {/* AI Edit Toolbar */}
                      <div className="d-flex align-items-center gap-2 mt-3">
                        {/* Enhance */}
                        <CButton
                          color="secondary"
                          variant="outline"
                          size="sm"
                          disabled={editImageLoading}
                          onClick={() => handleEdit("enhance")}
                        >
                          <FaMagic className="me-1" />
                          Enhance
                        </CButton>

                        {/* Remove BG */}
                        <CButton
                          color="secondary"
                          variant="outline"
                          size="sm"
                          disabled={editImageLoading}
                          onClick={() => handleEdit("remove_bg")}
                        >
                          <FaCut className="me-1" />
                          Remove BG
                        </CButton>

                        {/* Loader beside buttons */}
                        {editImageLoading && <CSpinner size="sm" />}
                      </div>

                      {/* Error / Success Feedback */}
                      {editImageError && (
                        <p className="text-danger mt-2">{editImageError}</p>
                      )}
                      {editImageSuccess && (
                        <p className="text-success mt-2">{editImageSuccess}</p>
                      )}
                    </CCol>
                  </CRow>

                  {/* Video Section */}
                  <CRow>
                    <CCol>
                      <CFormLabel className="mb-2">
                        Product Video (Live Video Verification)
                      </CFormLabel>
                      <LiveVideoCapture
                        productName={form.name}
                        onVideoCaptured={handleVideoCaptured}
                        defaultVideoUrl={
                          !videoWasDeleted ? productToEdit?.video_url : null
                        }
                      />
                      {kycType === "individual" && (
                        <div
                          style={{
                            color: "#d9534f",
                            fontSize: "0.9em",
                            marginTop: "0.5rem",
                          }}
                        >
                          *Note: Video upload is <strong>mandatory</strong> for
                          individual KYC users.
                        </div>
                      )}
                    </CCol>
                  </CRow>
                </CCardBody>
              </CCard>

              <CRow className="mb-4">
                <CCol>
                  <CCard className="p-3">
                    <h5 className="mb-3">Product Specifications</h5>

                    {form.specifications?.length > 0 ? (
                      form.specifications
                        .reduce((rows, spec, index) => {
                          if (index % 3 === 0) rows.push([]);
                          rows[rows.length - 1].push({ ...spec, index });
                          return rows;
                        }, [])
                        .map((group, rowIndex) => (
                          <CRow key={rowIndex} className="mb-3">
                            {group.map(
                              ({ label, value, input_type, index }) => (
                                <CCol md={4} key={index}>
                                  <CFormLabel>{label}</CFormLabel>
                                  <CFormInput
                                    type={input_type || "text"}
                                    placeholder={`Enter ${label}`}
                                    value={value}
                                    onChange={(e) => {
                                      const specs = [...form.specifications];
                                      specs[index].value = e.target.value;

                                      // Clear error for this field
                                      const updatedSpecErrors = {
                                        ...formErrors.specifications,
                                      };
                                      delete updatedSpecErrors[index];

                                      setForm({
                                        ...form,
                                        specifications: specs,
                                      });

                                      setFormErrors((prev) => ({
                                        ...prev,
                                        specifications: updatedSpecErrors,
                                      }));
                                    }}
                                  />
                                  {formErrors.specifications?.[index] && (
                                    <div
                                      style={{
                                        color: "red",
                                        fontSize: "0.9em",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {formErrors.specifications[index]}
                                    </div>
                                  )}
                                </CCol>
                              )
                            )}
                          </CRow>
                        ))
                    ) : (
                      <p className="text-muted">No specifications available.</p>
                    )}
                  </CCard>
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol md={12}>
                  <CCard className="p-3 position-relative">
                    <h5 className="mb-3">Frequently Asked Questions</h5>

                    <div className="mb-3 d-flex gap-2">
                      <CButton
                        color="secondary"
                        variant="outline"
                        onClick={() =>
                          setForm({
                            ...form,
                            faqs: [...form.faqs, { question: "", answer: "" }],
                          })
                        }
                      >
                        + Add New
                      </CButton>

                      {/* Auto Generate FAQs Button */}
                      <CButton
                        color="success"
                        variant="outline"
                        onClick={handleGenerateFaqs}
                        disabled={generatingFaqs}
                      >
                        {generatingFaqs ? (
                          <>
                            <CSpinner size="sm" className="me-1" />{" "}
                            Generating...
                          </>
                        ) : (
                          <>
                            <CIcon
                              icon={cilLightbulb}
                              size="sm"
                              className="me-1"
                            />
                            Suggest FAQs
                          </>
                        )}
                      </CButton>
                    </div>

                    {form.faqs.map((faq, index) => (
                      <CRow className="mb-3" key={index}>
                        <CCol md={5}>
                          <CFormInput
                            placeholder="Enter question"
                            value={faq.question}
                            onChange={(e) => {
                              const updatedFaqs = [...form.faqs];
                              updatedFaqs[index].question = e.target.value;
                              setForm({ ...form, faqs: updatedFaqs });
                            }}
                          />
                        </CCol>
                        <CCol md={5}>
                          <CFormInput
                            placeholder="Enter answer"
                            value={faq.answer}
                            onChange={(e) => {
                              const updatedFaqs = [...form.faqs];
                              updatedFaqs[index].answer = e.target.value;
                              setForm({ ...form, faqs: updatedFaqs });
                            }}
                          />
                        </CCol>
                        <CCol md={2} className="d-flex align-items-center">
                          <CButton
                            color="danger"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const updatedFaqs = form.faqs.filter(
                                (_, i) => i !== index
                              );
                              setForm({ ...form, faqs: updatedFaqs });
                            }}
                            title="Remove FAQ"
                          >
                            <FaTrash />
                          </CButton>
                        </CCol>
                      </CRow>
                    ))}

                    {/* FAQ loading overlay */}
                    {generatingFaqs && (
                      <div className="glass-container">
                        <div className="diamond-loader"></div>
                      </div>
                    )}
                  </CCard>
                </CCol>
              </CRow>

              <CRow>
                <CCol className="text-end mt-5">
                  <CButton
                    type="submit"
                    color="primary"
                    className="me-2"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <CSpinner size="sm" className="me-1" /> Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </CButton>
                  <CButton color="secondary" onClick={() => navigate(-1)}>
                    Cancel
                  </CButton>
                </CCol>
              </CRow>
            </CForm>
          </CCardBody>
        </CCard>
      </div>
      <AppToast
        visible={toastVisible}
        setVisible={setToastVisible}
        type={toastType}
        message={toastMessage}
      />
    </>
  );
};

export default AddNewProduct;
